import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, ChevronDown, Loader2, Sparkles, X } from 'lucide-react';
import useAppStore from '../store/appStore';
import useAdminStore from '../store/adminStore';
import { usePERentStore, type PERentLicenseType } from '../store/usePERentStore';
import {
    formatPremiumActivatedDate,
    formatPremiumCancelAvailableDate,
    formatPremiumTermEndDate,
    isPremiumCancelAllowed,
    registerPremiumToolBooking,
    resetAllPremiumToolBookings,
    resolvePremiumDualBooking,
} from '../utils/premiumCostBridge';
import PeFactsModal from './premium-tools/pe-facts/PeFactsModal';
import { PE_FACTS_TOOL_ID } from './premium-tools/pe-facts/types';

interface PremiumModalProps {
    showNotification: (msg: string, type?: 'success' | 'error') => void;
    navigateTo?: (pageKey: string) => void;
}

const PE_PAPERS_TOOL_ID = 'pe-papers';
const PE_PROFIT_TOOL_ID = 'pe-profit';
const PE_START_TOOL_ID = 'pe-start';
const PE_RENT_TOOL_ID = 'pe-rent';
const PE_SCOUT_TOOL_ID = 'pe-scout';
const PE_PLAN_TOOL_ID = 'pe-plan';
/** Patient Zero: Modal-only, kein Sidebar-/Page-Tree-Eintrag */
const PE_FACTS_ID = PE_FACTS_TOOL_ID;

const COACHING_PACKAGE_HINT =
    'Wichtig: Dieses Paket beinhaltet nicht nur den Zugang zum Tool, sondern auch die aktive, produktive Betreuung und Unterstützung durch deinen zuständigen Coach bei der Erstellung. Da diese detaillierte Planung in der Regel nicht ohne Expertenhilfe umgesetzt werden kann, kaufst du hier die direkte Begleitung und Expertise deines Betreuers mit ein.';

const CoachingPackageHintBox: React.FC<{ note?: string }> = ({ note }) => (
    <div className="rounded-xl border border-[#E6C299]/30 bg-[#E6C299]/10 px-4 py-4 mb-6">
        <p className="text-sm leading-relaxed text-slate-200">{note || COACHING_PACKAGE_HINT}</p>
    </div>
);

interface PremiumExtension {
    id: string;
    nameSuffix: string;
    description: string;
    /** Kurztext für Grid; Fallback: description */
    shortDescription?: string;
    bannerSrc?: string;
    category?: string;
    detailDescription?: string;
    features?: string[];
    comingSoon?: boolean;
    routeKey?: string;
    price?: number;
    badge?: string;
    coachingNote?: string;
    ctaButtonText?: string;
    /** Öffnet gekapseltes Modal statt Page-Navigation */
    openAsModal?: boolean;
}

const PREMIUM_EXTENSIONS: PremiumExtension[] = [
    {
        id: PE_PAPERS_TOOL_ID,
        nameSuffix: 'Papers',
        description:
            'Schluss mit rechtlichen Grauzonen: Generiere wasserdichte Arbeitsverträge, Abmahnungen und Zeugnisse in Sekunden.',
        shortDescription:
            'Rechtsdokumente für den Salon — 19,99 €/Monat oder 260 € einmal für 12 Monate.',
        bannerSrc: '/Premium-Tools/pe-papers-banner.png',
        category: 'Personal-Rechtsdokumente',
        routeKey: PE_PAPERS_TOOL_ID,
        price: 19.99,
        detailDescription:
            'Schluss mit rechtlichen Grauzonen. Generiere wasserdichte, tarifkonforme Arbeits- und Zusatzverträge in Sekunden statt Stunden.',
        features: [
            'Arbeitsverträge (inkl. mandantenspezifischem Leistungslohn)',
            'Kündigungen & Aufhebungsverträge',
            'Abmahnungen & Ermahnungen',
            'Arbeitszeugnisse & Urlaubskürzungen',
        ],
    },
    {
        id: PE_PROFIT_TOOL_ID,
        nameSuffix: 'Profit',
        description:
            'Bankenkonforme Rentabilitätsvorschau und Zukunftskalkulation. Analysiere deine langfristige Ertragskraft und nutze das integrierte Gewinnwarnsystem vor großen Investitionen.',
        shortDescription:
            'Rentabilitätsvorschau — 55 €/Monat oder 659 € einmal für 12 Monate.',
        bannerSrc: '/Premium-Tools/pe-profit.png',
        category: 'Gewinnanalyse',
        routeKey: PE_PROFIT_TOOL_ID,
        price: 55,
    },
    {
        id: PE_START_TOOL_ID,
        nameSuffix: 'Start',
        description:
            'Businessplan und Planungsanalyse für Existenzgründer und Salonübernahmen: Totpunkt, Ziel-Unternehmerlohn, Anlaufphase und bankenfähiger Businessplan inkl. Coach-Begleitung.',
        shortDescription:
            'Businessplan und Planungsanalyse für Existenzgründer und Salonübernahmen — 46 €/Monat oder 549 € einmal für 12 Monate — inkl. Coach-Begleitung.',
        bannerSrc: '/Premium-Tools/pe-start.png',
        category: 'BUSINESSPLAN & PLANUNGSANALYSE',
        price: 46,
        badge: 'NEU',
        routeKey: PE_START_TOOL_ID,
        detailDescription:
            'Businessplan und Planungsanalyse für Existenzgründer und Salonübernahmen. Totpunkt, Ziel-Unternehmerlohn, Anlaufmonate, Förderungen und bankenfähiger Businessplan — inklusive aktiver Coach-Begleitung.',
        coachingNote:
            'Wichtig: Dieses Paket beinhaltet nicht nur den Zugang zum Tool, sondern auch die aktive, produktive Betreuung und Unterstützung durch deinen zuständigen Coach bei der Erstellung. Da diese detaillierte Planung in der Regel nicht ohne Expertenhilfe umgesetzt werden kann, kaufst du hier die direkte Begleitung und Expertise deines Betreuers mit ein.',
        ctaButtonText: 'ZUM BUSINESSPLAN',
    },
    {
        id: PE_RENT_TOOL_ID,
        nameSuffix: 'Rent',
        description:
            'Kalkulator für Untervermietung, Stuhlmiete und Platzvermietung – berechne die exakte Wirtschaftlichkeit deiner vergebenen Salon-Flächen.',
        shortDescription:
            'Stuhlmiete & Platzvermietung — 14,99 €/Monat oder 199 €/Jahr, jeweils inkl. Coach-Betreuung.',
        bannerSrc: '/Premium-Tools/pe-rent.png',
        category: 'Salon-Kalkulation',
        price: 14.99,
        badge: 'NEU',
        routeKey: PE_RENT_TOOL_ID,
        detailDescription:
            'Berechne die wirtschaftlich korrekte Stuhlmiete aus deinen realen Salon-Gemeinkosten und produktiven Teamstunden. Mit Wochen-/Monatsmodus, optionalen Kostenblöcken und druckfertigem Nachweis.',
        features: [
            'Live-Anbindung an P1-Gemeinkosten & Teamstunden',
            'Wochen-, Monats- und Faktor-Eingabe',
            'Optionale Kostenblöcke (z. B. Material, Geräte)',
            'Druckfertige Kalkulationsübersicht',
            'Inklusive Coach-Betreuung bei der Umsetzung',
        ],
        coachingNote: COACHING_PACKAGE_HINT,
        ctaButtonText: 'ZUR STUHLMIETE-KALKULATION',
    },
    {
        id: PE_SCOUT_TOOL_ID,
        nameSuffix: 'Scout',
        description:
            'Mitarbeiterakquise für deinen Salon: Masterclass, KI-Stellenanzeigen, Bild-KI und Interview-Protokolle – damit du die richtigen Leute findest und einstellst.',
        shortDescription:
            'Mitarbeiterakquise: Leute finden und einstellen – Masterclass, KI-Stellenanzeigen, Bild-KI und Interview-Protokolle. 29,99 €/Monat oder 380 €/Jahr.',
        bannerSrc: '/Premium-Tools/pe-job.png',
        category: 'Mitarbeiterakquise · Recruiting',
        price: 29.99,
        badge: 'NEU',
        routeKey: PE_SCOUT_TOOL_ID,
        detailDescription:
            'PEScout ist dein Werkzeug für Mitarbeiterakquise – kein abstraktes „Scout“. Du bekommst ein vollwertiges Masterclass-Seminar zur Psychologie der Fachkräftegewinnung, einen KI-Textgenerator für echte Job-Ads ohne Floskeln, Bild-KI für Kampagnenmotive und fertige Interview-Protokolle (Fachkraft, Azubi, Assistenz, Rezeption) inkl. Totpunkt-/Sollumsatz-Check. Dazu der Leitfaden, wo und wie du die Anzeigen später veröffentlichst – lokal und smart.',
        features: [
            'Klar für Mitarbeiterakquise: Stellen besetzen, nicht raten',
            'Vollwertiges Masterclass-Seminar (Videos + Module 01–09)',
            'KI-Textgenerator für psychologische Job-Ads',
            'Bild-KI für Kampagnenmotive (Initial / Skalpell / Roulette)',
            'Interview-Navigator mit rollenspezifischen Gesprächsprotokollen',
            'Leitfaden: wo Anzeigen später laufen (FB-Gruppen, Kleinanzeigen, Arbeitsamt u. a.)',
        ],
        ctaButtonText: 'ZUR MITARBEITERAKQUISE',
    },
    {
        id: PE_FACTS_ID,
        nameSuffix: 'Facts',
        description:
            'Masterclass & KI-Aushang-Generator für Preisanpassungen – No-Sorry-Strategie und fertiger Aufsteller-Text.',
        shortDescription:
            'Preisanpassungen kommunizieren: Masterclass, KI-Aufsteller-Texter und No-Sorry-Strategie — 11,99 €/Monat oder 189 €/Jahr.',
        bannerSrc: '/Premium-Tools/pe-fakts.png',
        category: 'Preisstrategie · Kommunikation',
        badge: 'Premium',
        price: 11.99,
        routeKey: PE_FACTS_ID,
        openAsModal: true,
        detailDescription:
            'Masterclass zu Mindset und Auslastung plus KI-Generator für knappe, entschuldigungsfreie Preisanpassungs-Aufsteller.',
        features: [
            'Video-Masterclass Strategie & Mindset',
            '5 Module: Kalkulation, Zielgruppe, 20%-Rechnung, Auslastung, Aufsteller',
            'KI-Textgenerator (No-Sorry) inkl. Nachschärfen',
            'Kopieren & salonsicherer Druck',
        ],
        ctaButtonText: 'ZUM PREISANPASSUNGS-TEXTER',
    },
    {
        id: PE_PLAN_TOOL_ID,
        nameSuffix: 'Plan',
        description:
            'Vollwertiges Simulationswerkzeug für Inhaber, um Personalfluktuation, Abgänge mit Auffangquote, Inhaber-Rückzug, Zusatzinvestitionen und punktgenaue Preisanpassungen vorab risikofrei im Salon durchzuspielen.',
        shortDescription:
            'Echtzeit-Simulation für Personal, Inhaber-Stuhlzeit, Kosten & Preisanpassungen — 24,99 €/Monat oder 299,00 € Einmalkauf.',
        bannerSrc: '/Premium-Tools/pe-plan.png',
        category: 'PEPlan – Strategischer Planungsrechner',
        badge: 'NEU',
        price: 24.99,
        routeKey: PE_PLAN_TOOL_ID,
        detailDescription:
            'Vollwertiges Simulationswerkzeug für Inhaber, um Personalfluktuation, Abgänge mit Auffangquote, Inhaber-Rückzug, Zusatzinvestitionen und punktgenaue Preisanpassungen vorab risikofrei im Salon durchzuspielen.',
        features: [
            '5 Szenario-Hebel: Neuanstellung, Kündigung & Team-Auffangquote, Inhaber-Stuhlzeit, Investitionen, Preiserhöhung',
            'Automatisch austarierter Kostendeckungsfaktor (KDF) & reaktiver Lohnfaktor',
            'Gesamtsalon-Umsatzziele (Netto & Brutto, mit und ohne 10 % Rücklagenbindung)',
            'Spiegelung der neuen Kennzahlen auf alle Mitarbeiter-Sollumsätze (bei 75 % Auslastung)',
            'Integrierter Dienstleistungs-Kalkulator mit Materialmitnahme und Preis-Barometer',
        ],
        ctaButtonText: 'ZUM PLANUNGSRECHNER',
    },
];

const renderPremiumToolTitle = (nameSuffix: string, className = 'text-xl') => (
    <div className={`${className} text-[#E6C299] mb-2`}>
        <span className="italic">PE</span>
        <span className="font-bold">{nameSuffix}</span>
    </div>
);

const ABO_HINT =
    'Preisvorteil: günstiger Einstieg ohne hohe Einmalbelastung. Das Abo verlängert sich ab Freischaltung automatisch und ist nach 12 Monaten monatlich kündbar.';
const YEARLY_HINT =
    'Voller Zugriff für 12 Monate in einer Zahlung. Keine automatische Verlängerung.';

type DualPlanId = 'monthly' | 'yearly';

type DualPlan = {
    id: DualPlanId;
    label: string;
    price: string;
    suffix: string;
    hint: string;
    recommended?: boolean;
    advantageBadge?: string;
};

const PE_PAPERS_PLANS: DualPlan[] = [
    {
        id: 'monthly',
        label: 'Monatsabo',
        price: '19,99 €',
        suffix: '/ Monat',
        hint: ABO_HINT,
        recommended: true,
        advantageBadge: 'Empfohlen & Preisvorteil',
    },
    {
        id: 'yearly',
        label: 'Einmalzahlung',
        price: '260 €',
        suffix: '/ Jahr',
        hint: YEARLY_HINT,
    },
];

const PE_PROFIT_PLANS: DualPlan[] = [
    {
        id: 'monthly',
        label: 'Monatsabo',
        price: '55 €',
        suffix: '/ Monat',
        hint: ABO_HINT,
        recommended: true,
        advantageBadge: 'Empfohlen & Preisvorteil',
    },
    {
        id: 'yearly',
        label: 'Einmalzahlung',
        price: '659 €',
        suffix: '/ Jahr',
        hint: YEARLY_HINT,
    },
];

const PE_START_PLANS: DualPlan[] = [
    {
        id: 'monthly',
        label: 'Monatsabo',
        price: '46 €',
        suffix: '/ Monat',
        hint: ABO_HINT,
        recommended: true,
        advantageBadge: 'Empfohlen & Preisvorteil',
    },
    {
        id: 'yearly',
        label: 'Einmalzahlung',
        price: '549 €',
        suffix: '/ Jahr',
        hint: YEARLY_HINT,
    },
];

const PE_SCOUT_PLANS: DualPlan[] = [
    {
        id: 'monthly',
        label: 'Monatsabo',
        price: '29,99 €',
        suffix: '/ Monat',
        hint: ABO_HINT,
        recommended: true,
        advantageBadge: 'Empfohlen & Preisvorteil',
    },
    {
        id: 'yearly',
        label: 'Einmalzahlung',
        price: '380 €',
        suffix: '/ Jahr',
        hint: YEARLY_HINT,
    },
];

const PE_FACTS_PLANS: DualPlan[] = [
    {
        id: 'monthly',
        label: 'Monatsabo',
        price: '11,99 €',
        suffix: '/ Monat',
        hint: ABO_HINT,
        recommended: true,
        advantageBadge: 'Empfohlen & Preisvorteil',
    },
    {
        id: 'yearly',
        label: 'Einmalzahlung',
        price: '189 €',
        suffix: '/ Jahr',
        hint: YEARLY_HINT,
    },
];

const PE_PLAN_PLANS: DualPlan[] = [
    {
        id: 'monthly',
        label: 'Monats-Lizenz',
        price: '24,99 €',
        suffix: 'netto / Monat',
        hint: ABO_HINT,
        recommended: true,
        advantageBadge: 'Empfohlen & Preisvorteil',
    },
    {
        id: 'yearly',
        label: 'Einmalkauf (Lifetime / Jahresbeitrag)',
        price: '299,00 €',
        suffix: 'netto einmalig',
        hint: YEARLY_HINT,
    },
];

type PERentPlanId = Exclude<PERentLicenseType, null>;

const PE_RENT_PLANS: {
    id: PERentPlanId;
    label: string;
    price: string;
    suffix: string;
    hint: string;
    recommended?: boolean;
    advantageBadge?: string;
}[] = [
    {
        id: 'sub_24',
        label: 'Monatsabo',
        price: '14,99 €',
        suffix: '/ Monat',
        hint: `${ABO_HINT} Inklusive Coach-Betreuung für PERent.`,
        recommended: true,
        advantageBadge: 'Empfohlen & Preisvorteil',
    },
    {
        id: 'single_300',
        label: 'Einmalzahlung',
        price: '199 €',
        suffix: '/ Jahr',
        hint: `${YEARLY_HINT} Inklusive Coach-Betreuung für PERent.`,
    },
];

const PremiumModalStyles = () => (
    <style>{`
        @keyframes premium-border-shine {
            0% { background-position: 0% 50%; }
            50% { background-position: 100% 50%; }
            100% { background-position: 0% 50%; }
        }
        .premium-card-border {
            background: linear-gradient(90deg, #E6C299, #f0d3b3, #b48a56, #E6C299, #f0d3b3);
            background-size: 300% 300%;
            animation: premium-border-shine 4s ease infinite;
        }
    `}</style>
);

const CHECKOUT_BUTTON_CLASS =
    'w-full py-3 rounded-xl font-black text-[10px] uppercase tracking-widest bg-gradient-to-b from-[#E6C299] to-[#b48a56] hover:from-[#f0d3b3] hover:to-[#cfa674] text-black transition-all shadow-[0_0_20px_rgba(230,194,153,0.25)] disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2';

const PremiumModal: React.FC<PremiumModalProps> = ({ showNotification, navigateTo }) => {
    const {
        isPremiumModalOpen,
        closePremiumModal,
        unlockTool,
        cancelSubscription,
        adminForceSetPremiumTool,
        unlockedPremiumTools,
        premiumModalFocusToolId,
        clientData,
    } = useAppStore((state) => ({
        isPremiumModalOpen: state.isPremiumModalOpen,
        closePremiumModal: state.closePremiumModal,
        unlockTool: state.unlockTool,
        cancelSubscription: state.cancelSubscription,
        adminForceSetPremiumTool: state.adminForceSetPremiumTool,
        unlockedPremiumTools: state.unlockedPremiumTools,
        premiumModalFocusToolId: state.premiumModalFocusToolId,
        clientData: state.clientData,
    }));

    const impersonatedClientId = useAdminStore((s) => s.impersonatedClientId);
    const isGodMode =
        Boolean(impersonatedClientId) ||
        Boolean((clientData as { _isGodMode?: boolean } | null)?._isGodMode) ||
        (typeof sessionStorage !== 'undefined' && Boolean(sessionStorage.getItem('GOD_MODE_ID')));

    const [isCheckingOut, setIsCheckingOut] = useState(false);
    const [isCancelling, setIsCancelling] = useState(false);
    const [isResettingBookings, setIsResettingBookings] = useState(false);
    const [selectedPapersPlan, setSelectedPapersPlan] = useState<DualPlanId>('monthly');
    const [selectedProfitPlan, setSelectedProfitPlan] = useState<DualPlanId>('monthly');
    const [selectedStartPlan, setSelectedStartPlan] = useState<DualPlanId>('monthly');
    const [selectedRentPlan, setSelectedRentPlan] = useState<PERentPlanId>('sub_24');
    const [selectedScoutPlan, setSelectedScoutPlan] = useState<DualPlanId>('monthly');
    const [selectedFactsPlan, setSelectedFactsPlan] = useState<DualPlanId>('monthly');
    const [selectedPlanPlan, setSelectedPlanPlan] = useState<DualPlanId>('monthly');
    const [viewingToolId, setViewingToolId] = useState<string | null>(null);
    const [isPeFactsOpen, setIsPeFactsOpen] = useState(false);
    /** Gottmodus-Leiste zugeklappt — beim Vorführen nicht im Weg */
    const [isGodModePanelOpen, setIsGodModePanelOpen] = useState(false);

    const setPERentLicense = usePERentStore((s) => s.setLicense);
    const peRentLicenseType = usePERentStore((s) => s.licenseType);
    const isPePapersUnlocked = unlockedPremiumTools.includes(PE_PAPERS_TOOL_ID);
    const viewingTool = PREMIUM_EXTENSIONS.find((tool) => tool.id === viewingToolId) ?? null;

    const openPremiumTool = (routeKey: string) => {
        closePremiumModal();
        setViewingToolId(null);
        if (navigateTo) {
            navigateTo(routeKey);
            return;
        }
        window.history.pushState({}, '', `/${routeKey}`);
    };

    useEffect(() => {
        if (!isPremiumModalOpen) {
            setViewingToolId(null);
            return;
        }
        if (premiumModalFocusToolId) {
            setViewingToolId(premiumModalFocusToolId);
        }
    }, [isPremiumModalOpen, premiumModalFocusToolId]);

    // Gebucht, aber ohne Laufzeit-Datum → automatisch reparieren (kein Neu-Buchen nötig)
    useEffect(() => {
        if (!isPremiumModalOpen) return;
        void (async () => {
            try {
                const { repairMissingPremiumActivationDates } = await import(
                    '../utils/premiumCostBridge'
                );
                await repairMissingPremiumActivationDates();
            } catch {
                /* ignore */
            }
        })();
    }, [isPremiumModalOpen]);

    if (!isPremiumModalOpen) return null;

    const handleDualCheckout = async (
        toolId: string,
        plan: DualPlanId,
        label: string
    ) => {
        setIsCheckingOut(true);
        await new Promise((resolve) => setTimeout(resolve, 800));
        try {
            const booking = resolvePremiumDualBooking(toolId, plan);
            if (!booking) throw new Error('Unbekanntes Tool');
            await registerPremiumToolBooking(booking);
            unlockTool(toolId);
            showNotification(
                plan === 'yearly'
                    ? `${label} freigeschaltet (Einmalzahlung / Jahr).`
                    : `${label} freigeschaltet (Monatsabo).`,
                'success'
            );
        } catch (err) {
            console.error(err);
            showNotification('Freischaltung fehlgeschlagen.', 'error');
        }
        setIsCheckingOut(false);
    };

    const handleManualResetBookings = async () => {
        setIsResettingBookings(true);
        try {
            await resetAllPremiumToolBookings();
            showNotification(
                'Alle Premium-Buchungen zurückgesetzt — bitte neu freischalten.',
                'success'
            );
        } catch {
            showNotification('Zurücksetzen fehlgeschlagen.', 'error');
        }
        setIsResettingBookings(false);
    };

    const renderDualPlanGrid = (
        plans: DualPlan[],
        selected: DualPlanId,
        onSelect: (id: DualPlanId) => void,
        mode: 'select' | 'readonly',
        activePlan?: DualPlanId | null
    ) => (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {plans.map((plan) => {
                const isSelected =
                    mode === 'select' ? selected === plan.id : activePlan === plan.id;
                const highlight = isSelected || (mode === 'readonly' && !!activePlan && activePlan === plan.id) || (mode === 'readonly' && !activePlan && !!plan.recommended);
                const className = `relative rounded-xl px-4 py-4 text-left transition-all ${
                    highlight
                        ? 'border-2 border-[#E6C299] bg-[#E6C299]/15 shadow-[0_0_20px_rgba(230,194,153,0.2)]'
                        : 'border border-slate-700 bg-[#0a0b10] hover:border-slate-500'
                }`;
                const body = (
                    <>
                        {plan.recommended && plan.advantageBadge ? (
                            <span className="absolute -top-2.5 right-2 max-w-[calc(100%-0.5rem)] whitespace-normal text-center px-2 py-0.5 rounded-full bg-[#E6C299] text-black text-[10px] font-black tracking-wide leading-tight">
                                {plan.advantageBadge}
                            </span>
                        ) : plan.recommended ? (
                            <span className="absolute -top-2 right-3 whitespace-nowrap px-2 py-0.5 rounded-full bg-[#E6C299] text-black text-[10px] font-black tracking-wide">
                                Empfohlen
                            </span>
                        ) : null}
                        <p
                            className={`text-xs uppercase tracking-widest mb-1 font-bold ${
                                highlight ? 'text-[#E6C299]' : 'text-slate-400'
                            }`}
                        >
                            {plan.label}
                        </p>
                        <p className="text-lg font-black text-white leading-tight">
                            {plan.price}
                            <span className="text-xs font-semibold text-slate-400">
                                {' '}
                                {plan.suffix}
                            </span>
                        </p>
                        <p className="text-sm text-slate-300 mt-2 leading-relaxed">{plan.hint}</p>
                    </>
                );
                if (mode === 'select') {
                    return (
                        <button
                            key={plan.id}
                            type="button"
                            onClick={() => onSelect(plan.id)}
                            className={className}
                        >
                            {body}
                        </button>
                    );
                }
                return (
                    <div key={plan.id} className={className}>
                        {body}
                    </div>
                );
            })}
        </div>
    );

    const getToolActivatedAt = (toolId: string): string | null => {
        const map = (clientData as { premiumToolActivatedAt?: Record<string, string> } | null)
            ?.premiumToolActivatedAt;
        if (!map || typeof map !== 'object') return null;
        return map[toolId] || null;
    };

    const getToolPlanType = (toolId: string): 'monthly' | 'yearly' | null => {
        const map = (clientData as { premiumToolPlanType?: Record<string, string> } | null)
            ?.premiumToolPlanType;
        if (!map || typeof map !== 'object') return null;
        const v = map[toolId];
        return v === 'monthly' || v === 'yearly' ? v : null;
    };

    const handleCancelTool = async (toolId: string, toolLabel: string) => {
        const activatedAt = getToolActivatedAt(toolId);
        if (!isPremiumCancelAllowed(activatedAt)) {
            showNotification(
                `Kündigung erst ab ${formatPremiumCancelAvailableDate(activatedAt)} möglich (nach 12 Monaten).`,
                'error'
            );
            return;
        }
        setIsCancelling(true);
        await new Promise((resolve) => setTimeout(resolve, 800));
        cancelSubscription(toolId);
        showNotification(`${toolLabel}-Abo gekündigt. Coach wird im Change-Radar informiert.`, 'success');
        closePremiumModal();
        setIsCancelling(false);
    };

    const renderCancelSection = (toolId: string, toolLabel: string) => {
        const activatedAt = getToolActivatedAt(toolId);
        const planType = getToolPlanType(toolId);
        const isYearlyPurchase =
            planType === 'yearly' ||
            (toolId === PE_RENT_TOOL_ID && peRentLicenseType === 'single_300');
        const canCancel = isPremiumCancelAllowed(activatedAt);
        const termEnd = formatPremiumTermEndDate(activatedAt);
        const activated = formatPremiumActivatedDate(activatedAt);
        const hasRealActivation = Boolean(activatedAt && Date.parse(activatedAt));

        if (!hasRealActivation) {
            return (
                <div className="rounded-xl border border-amber-700/50 bg-amber-950/20 px-4 py-3 text-left space-y-1">
                    <p className="text-xs font-bold text-amber-200">
                        Freischaltung aktiv — Laufzeitdatum fehlt in der Firebase-Akte
                    </p>
                    <p className="text-[11px] text-slate-400 leading-relaxed">
                        Es wird kein Ersatzdatum gesetzt. Beim nächsten Laden wird versucht, das echte
                        Datum aus Change-Radar / Akte wiederherzustellen. Sonst Support kontaktieren.
                    </p>
                </div>
            );
        }

        if (isYearlyPurchase) {
            return (
                <div className="rounded-xl border border-slate-700 bg-[#0a0b10] px-4 py-3 text-left space-y-1">
                    <p className="text-xs text-slate-300">
                        Freigeschaltet am{' '}
                        <span className="font-bold text-[#E6C299]">{activated}</span>
                    </p>
                    <p className="text-xs text-slate-300">
                        Laufzeit bis{' '}
                        <span className="font-bold text-[#E6C299]">{termEnd}</span>
                    </p>
                    <p className="text-[11px] text-slate-500 leading-relaxed">
                        Einmalzahlung für 12 Monate — keine automatische Verlängerung. Danach erneut
                        lizenzieren.
                    </p>
                </div>
            );
        }

        if (canCancel) {
            return (
                <div className="space-y-3">
                    <div className="rounded-xl border border-slate-700 bg-[#0a0b10] px-4 py-3 text-left space-y-1">
                        <p className="text-xs text-slate-300">
                            Freigeschaltet am{' '}
                            <span className="font-bold text-[#E6C299]">{activated}</span>
                        </p>
                        <p className="text-xs text-slate-300">
                            Mindestlaufzeit bis{' '}
                            <span className="font-bold text-[#E6C299]">{termEnd}</span>
                        </p>
                        <p className="text-[11px] text-slate-500 leading-relaxed">
                            Das Abo verlängert sich ab Freischaltung automatisch und ist jetzt monatlich
                            kündbar.
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={() => void handleCancelTool(toolId, toolLabel)}
                        disabled={isCancelling}
                        className="w-full py-3 rounded-xl font-bold text-xs uppercase tracking-widest border border-red-500/40 bg-red-500/10 text-red-400 hover:bg-red-500/20 transition-colors disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                        {isCancelling ? (
                            <>
                                <Loader2 size={16} className="animate-spin" />
                                Wird gekündigt...
                            </>
                        ) : (
                            `${toolLabel}-Abo kündigen`
                        )}
                    </button>
                </div>
            );
        }

        return (
            <div className="rounded-xl border border-slate-700 bg-[#0a0b10] px-4 py-3 text-left space-y-1">
                <p className="text-xs text-slate-300">
                    Freigeschaltet am{' '}
                    <span className="font-bold text-[#E6C299]">{activated}</span>
                </p>
                <p className="text-xs text-slate-300">
                    Laufzeit / Mindestlaufzeit bis{' '}
                    <span className="font-bold text-[#E6C299]">{termEnd}</span>
                </p>
                <p className="text-xs text-slate-300">
                    Kündigung möglich ab{' '}
                    <span className="font-bold text-[#E6C299]">{termEnd}</span>
                </p>
                <p className="text-[11px] text-slate-500 leading-relaxed pt-1">
                    Das Abo verlängert sich ab dem Moment der Freischaltung automatisch und ist nach
                    Ablauf der 12 Monate monatlich kündbar.
                </p>
            </div>
        );
    };

    const renderPapersCheckoutButton = () => (
        <button
            type="button"
            onClick={() => void handleDualCheckout(PE_PAPERS_TOOL_ID, selectedPapersPlan, 'PEPapers')}
            disabled={isCheckingOut}
            className={CHECKOUT_BUTTON_CLASS}
        >
            {isCheckingOut ? (
                <>
                    <Loader2 size={16} className="animate-spin" />
                    Wird freigeschaltet...
                </>
            ) : selectedPapersPlan === 'yearly' ? (
                'Einmalzahlung wählen (Mock-Checkout)'
            ) : (
                'Monatsabo aktivieren (Mock-Checkout)'
            )}
        </button>
    );

    return createPortal(
        <>
            <PremiumModalStyles />
            <div
                className="fixed inset-0 bg-black/90 backdrop-blur-md z-[100000] flex items-center justify-center p-4"
                onClick={closePremiumModal}
            >
                <div
                    className="relative w-full max-w-5xl max-h-[90vh] overflow-y-auto bg-[#0f1015] border border-[#E6C299]/20 rounded-2xl shadow-[0_0_60px_rgba(230,194,153,0.15)] p-6 md:p-8 custom-scrollbar"
                    onClick={(e) => e.stopPropagation()}
                >
                    <button
                        type="button"
                        onClick={closePremiumModal}
                        className="absolute top-4 right-4 text-slate-400 hover:text-white bg-[#1a1a1a] hover:bg-[#222] rounded-lg p-2 transition-colors border border-[#333]"
                        aria-label="Schließen"
                    >
                        <X className="w-4 h-4" />
                    </button>

                    <div className="text-center mb-8 pr-8">
                        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[#E6C299]/30 bg-[#E6C299]/10 text-[#E6C299] text-xs font-black uppercase tracking-widest mb-4">
                            <Sparkles size={14} />
                            Premium-Tools
                        </div>
                        <h2 className="text-2xl md:text-3xl font-black text-[#E6C299] uppercase tracking-widest">
                            Exklusive Erweiterungen
                        </h2>
                        <p className="text-slate-400 text-sm mt-3 max-w-xl mx-auto leading-relaxed">
                            Schalte professionelle Zusatzmodule frei und hebe dein Salon-Management auf das nächste Level.
                        </p>
                        {isGodMode && (
                            <button
                                type="button"
                                onClick={() => void handleManualResetBookings()}
                                disabled={isResettingBookings}
                                className="mt-4 text-[10px] uppercase tracking-widest font-bold text-slate-500 hover:text-red-400 underline disabled:opacity-50"
                            >
                                {isResettingBookings
                                    ? 'Buchungen werden zurückgesetzt…'
                                    : 'Alle Premium-Buchungen zurücksetzen (Neutest)'}
                            </button>
                        )}
                    </div>

                    {isGodMode && viewingToolId === null && (
                        <div className="mb-6 rounded-2xl border border-amber-500/40 bg-amber-500/10 overflow-hidden">
                            <button
                                type="button"
                                onClick={() => setIsGodModePanelOpen((o) => !o)}
                                className="w-full flex items-center justify-between gap-3 px-4 md:px-5 py-3 text-left hover:bg-amber-500/10 transition-colors"
                                aria-expanded={isGodModePanelOpen}
                            >
                                <span className="text-[10px] font-black uppercase tracking-[0.2em] text-amber-300">
                                    Gottmodus · Demo-Freischaltung
                                </span>
                                <span className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-wider text-amber-200/70">
                                    {isGodModePanelOpen ? 'Zuklappen' : 'Aufklappen'}
                                    <ChevronDown
                                        size={16}
                                        className={`shrink-0 transition-transform ${isGodModePanelOpen ? 'rotate-180' : ''}`}
                                    />
                                </span>
                            </button>
                            {isGodModePanelOpen && (
                                <div className="px-4 md:px-5 pb-4 md:pb-5 pt-1 border-t border-amber-500/25">
                                    <p className="mb-4 text-sm leading-relaxed text-amber-100/80">
                                        Premium-Tools für diesen Mandanten sofort an- oder ausschalten — ohne
                                        Stripe, ohne 12-Monats-Frist. Ideal zum Vorführen mit echten Daten.
                                    </p>
                                    <div className="space-y-2">
                                        {PREMIUM_EXTENSIONS.map((tool) => {
                                            const active = unlockedPremiumTools.includes(tool.id);
                                            return (
                                                <div
                                                    key={`god-${tool.id}`}
                                                    className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-amber-500/25 bg-black/30 px-3 py-2.5"
                                                >
                                                    <div className="min-w-0 text-left">
                                                        <p className="text-sm font-bold text-white">
                                                            PE{tool.nameSuffix}
                                                        </p>
                                                        <p className="text-[11px] text-amber-100/60">
                                                            {active ? 'Aktuell freigeschaltet' : 'Gesperrt'}
                                                        </p>
                                                    </div>
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            adminForceSetPremiumTool(tool.id, !active);
                                                            showNotification(
                                                                active
                                                                    ? `PE${tool.nameSuffix} deaktiviert (Gottmodus)`
                                                                    : `PE${tool.nameSuffix} freigeschaltet (Gottmodus)`,
                                                                'success'
                                                            );
                                                        }}
                                                        className={`shrink-0 rounded-lg px-3 py-2 text-[10px] font-black uppercase tracking-wider transition-colors ${
                                                            active
                                                                ? 'border border-rose-400/50 bg-rose-500/20 text-rose-200 hover:bg-rose-500/30'
                                                                : 'border border-emerald-400/50 bg-emerald-500/20 text-emerald-200 hover:bg-emerald-500/30'
                                                        }`}
                                                    >
                                                        {active ? 'Deaktivieren' : 'Aktivieren'}
                                                    </button>
                                                </div>
                                            );
                                        })}
                                    </div>

                                    <div className="mt-5 border-t border-amber-500/25 pt-4">
                                        <p className="mb-1 text-[10px] font-black uppercase tracking-[0.2em] text-amber-300">
                                            Coach-Menü
                                        </p>
                                        <p className="text-sm leading-relaxed text-amber-100/80">
                                            PEPlan schaltest du oben bei den Premium-Tools frei — danach erscheint es unter
                                            Analyse Tools. Der interne Planungsrechner (lime) ist nur im Gottmodus sichtbar.
                                            Solo-Simulator bleibt standardmäßig aus und wird bei Bedarf über die Menü-Freigabe
                                            auf der System-Startseite zugeschaltet.
                                        </p>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {viewingToolId === null ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-stretch">
                            {PREMIUM_EXTENSIONS.map((tool) => (
                                <div key={tool.id} className="premium-card-border rounded-2xl p-[2px] h-full">
                                    <div className="bg-[#111622] rounded-2xl overflow-hidden flex flex-col h-full">
                                        <div className="relative w-full h-40 overflow-hidden shrink-0">
                                            {tool.bannerSrc ? (
                                                <img
                                                    src={tool.bannerSrc}
                                                    alt={`PE${tool.nameSuffix} Premium Artwork`}
                                                    className={`w-full h-full object-cover rounded-t-md ${
                                                        tool.id === PE_PAPERS_TOOL_ID
                                                            ? 'scale-[1.12] object-[center_28%]'
                                                            : 'object-center'
                                                    }`}
                                                />
                                            ) : (
                                                <div className="w-full h-40 bg-gradient-to-br from-[#0a0b10] via-[#111622] to-[#0f1015] border-b border-[#E6C299]/10 flex items-center justify-center rounded-t-md">
                                                    {renderPremiumToolTitle(tool.nameSuffix, 'text-2xl')}
                                                </div>
                                            )}
                                            {tool.comingSoon && (
                                                <span className="absolute top-3 right-3 z-10 bg-black/75 backdrop-blur-sm border border-[#E6C299]/40 text-[#E6C299] text-[9px] font-black uppercase tracking-[0.2em] px-2.5 py-1 rounded-md">
                                                    COMING SOON
                                                </span>
                                            )}
                                            {!tool.comingSoon && unlockedPremiumTools.includes(tool.id) && (
                                                <span className="absolute top-3 right-3 z-10 bg-emerald-500/90 backdrop-blur-sm border border-emerald-300/40 text-white text-[9px] font-black uppercase tracking-[0.2em] px-2.5 py-1 rounded-md">
                                                    AKTIV
                                                </span>
                                            )}
                                            {!tool.comingSoon &&
                                                !unlockedPremiumTools.includes(tool.id) &&
                                                tool.badge && (
                                                    <span className="absolute top-3 right-3 z-10 bg-black/75 backdrop-blur-sm border border-[#E6C299]/40 text-[#E6C299] text-[9px] font-black uppercase tracking-[0.2em] px-2.5 py-1 rounded-md">
                                                        {tool.badge}
                                                    </span>
                                                )}
                                        </div>
                                        <div className="p-4 md:p-5 flex flex-col flex-1 min-h-[11.5rem]">
                                            {renderPremiumToolTitle(tool.nameSuffix)}
                                            {tool.category && (
                                                <p className="text-[10px] text-slate-500 uppercase tracking-widest mb-2">
                                                    {tool.category}
                                                </p>
                                            )}
                                            <p className="text-slate-300 text-sm leading-relaxed mb-4 flex-1">
                                                {tool.shortDescription ?? tool.description}
                                            </p>
                                            <button
                                                type="button"
                                                onClick={() => setViewingToolId(tool.id)}
                                                className={`w-full py-2.5 rounded-xl font-bold text-xs uppercase tracking-widest transition-colors mt-auto ${
                                                    unlockedPremiumTools.includes(tool.id)
                                                        ? 'border border-emerald-500/40 bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20'
                                                        : 'border border-[#E6C299]/40 bg-[#E6C299]/10 text-[#E6C299] hover:bg-[#E6C299]/20'
                                                }`}
                                            >
                                                {unlockedPremiumTools.includes(tool.id) ? 'Öffnen / Verwalten' : 'Mehr erfahren'}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : viewingToolId === PE_PAPERS_TOOL_ID ? (
                        <div className="max-w-2xl mx-auto w-full">
                            <div className="premium-card-border rounded-2xl p-[2px]">
                                <div className="bg-[#111622] rounded-2xl p-6 md:p-8">
                                    <button
                                        type="button"
                                        onClick={() => setViewingToolId(null)}
                                        className="mb-5 text-slate-400 hover:text-[#E6C299] text-sm transition-colors"
                                    >
                                        ← Zurück zur Übersicht
                                    </button>

                                    <img
                                        src="/Premium-Tools/pe-papers-banner.png"
                                        alt="PE-Papers Premium Artwork"
                                        className="w-full h-48 sm:h-56 object-cover rounded-md mb-5"
                                    />

                                    <div className="text-2xl text-[#E6C299] mb-1">
                                        <span className="italic">PE</span>
                                        <span className="font-bold">Papers</span>
                                    </div>
                                    <p className="text-xs text-slate-500 uppercase tracking-widest mb-3">Personal-Rechtsdokumente</p>
                                    <p className="text-slate-300 text-sm leading-relaxed mb-4">
                                        Schluss mit rechtlichen Grauzonen. Generiere wasserdichte, tarifkonforme Arbeits- und
                                        Zusatzverträge in Sekunden statt Stunden.
                                    </p>

                                    <CoachingPackageHintBox />

                                    <ul className="text-slate-400 text-sm space-y-2 mb-6">
                                        <li>✓ Arbeitsverträge (inkl. mandantenspezifischem Leistungslohn)</li>
                                        <li>✓ Kündigungen & Aufhebungsverträge</li>
                                        <li>✓ Abmahnungen & Ermahnungen</li>
                                        <li>✓ Arbeitszeugnisse & Urlaubskürzungen</li>
                                    </ul>

                                    {isPePapersUnlocked ? (
                                    <div className="space-y-4">
                                        <div className="rounded-xl border border-emerald-500/40 bg-emerald-500/10 px-4 py-4 text-center">
                                            <p className="text-emerald-400 font-bold text-sm">✅ Erfolgreich aktiviert.</p>
                                            {getToolPlanType(PE_PAPERS_TOOL_ID) && (
                                                <p className="mt-1 text-xs text-emerald-200/80">
                                                    Aktiver Tarif:{' '}
                                                    {getToolPlanType(PE_PAPERS_TOOL_ID) === 'yearly'
                                                        ? '260 € Einmalzahlung / Jahr'
                                                        : '19,99 € Monatsabo'}
                                                </p>
                                            )}
                                        </div>
                                        <div>
                                            <p className="mb-2 text-[10px] font-bold uppercase tracking-widest text-slate-400">
                                                Tarifübersicht
                                            </p>
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                                {PE_PAPERS_PLANS.map((plan) => {
                                                    const isActive =
                                                        getToolPlanType(PE_PAPERS_TOOL_ID) === plan.id;
                                                    return (
                                                    <div
                                                        key={plan.id}
                                                        className={`relative rounded-xl px-4 py-4 text-left border ${
                                                            isActive || plan.recommended
                                                                ? 'border-2 border-[#E6C299] bg-[#E6C299]/15'
                                                                : 'border-slate-700 bg-[#0a0b10]'
                                                        }`}
                                                    >
                                                        {plan.recommended && plan.advantageBadge ? (
                                                            <span className="absolute -top-2.5 right-2 max-w-[calc(100%-0.5rem)] whitespace-normal text-center px-2 py-0.5 rounded-full bg-[#E6C299] text-black text-[10px] font-black tracking-wide leading-tight">
                                                                {plan.advantageBadge}
                                                            </span>
                                                        ) : plan.recommended ? (
                                                            <span className="absolute -top-2 right-3 whitespace-nowrap px-2 py-0.5 rounded-full bg-[#E6C299] text-black text-[10px] font-black tracking-wide">
                                                                Empfohlen
                                                            </span>
                                                        ) : null}
                                                        <p className="text-xs uppercase tracking-widest mb-1 font-bold text-[#E6C299]">
                                                            {plan.label}
                                                        </p>
                                                        <p className="text-lg font-black text-white leading-tight">
                                                            {plan.price}
                                                            <span className="text-xs font-semibold text-slate-400">
                                                                {' '}
                                                                {plan.suffix}
                                                            </span>
                                                        </p>
                                                        <p className="text-sm text-slate-300 mt-2 leading-relaxed">
                                                            {plan.hint}
                                                        </p>
                                                    </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => openPremiumTool(PE_PAPERS_TOOL_ID)}
                                            className={CHECKOUT_BUTTON_CLASS}
                                        >
                                            PEPapers öffnen
                                        </button>
                                        {renderCancelSection(PE_PAPERS_TOOL_ID, 'PEPapers')}
                                    </div>
                                ) : (
                                    <div className="space-y-4">
                                        <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
                                            Tarif wählen
                                        </p>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                            {PE_PAPERS_PLANS.map((plan) => {
                                                const isSelected = selectedPapersPlan === plan.id;
                                                return (
                                                    <button
                                                        key={plan.id}
                                                        type="button"
                                                        onClick={() => setSelectedPapersPlan(plan.id)}
                                                        className={`relative rounded-xl px-4 py-4 text-left transition-all ${
                                                            isSelected
                                                                ? 'border-2 border-[#E6C299] bg-[#E6C299]/15 shadow-[0_0_20px_rgba(230,194,153,0.2)]'
                                                                : 'border border-slate-700 bg-[#0a0b10] hover:border-slate-500'
                                                        }`}
                                                    >
                                                        {plan.recommended && plan.advantageBadge ? (
                                                            <span className="absolute -top-2.5 right-2 max-w-[calc(100%-0.5rem)] whitespace-normal text-center px-2 py-0.5 rounded-full bg-[#E6C299] text-black text-[10px] font-black tracking-wide leading-tight">
                                                                {plan.advantageBadge}
                                                            </span>
                                                        ) : plan.recommended ? (
                                                            <span className="absolute -top-2 right-3 whitespace-nowrap px-2 py-0.5 rounded-full bg-[#E6C299] text-black text-[10px] font-black tracking-wide">
                                                                Empfohlen
                                                            </span>
                                                        ) : null}
                                                        <p
                                                            className={`text-xs uppercase tracking-widest mb-1 font-bold ${
                                                                isSelected ? 'text-[#E6C299]' : 'text-slate-400'
                                                            }`}
                                                        >
                                                            {plan.label}
                                                        </p>
                                                        <p className="text-lg font-black text-white leading-tight">
                                                            {plan.price}
                                                            <span className="text-xs font-semibold text-slate-400">
                                                                {' '}
                                                                {plan.suffix}
                                                            </span>
                                                        </p>
                                                        <p className="text-sm text-slate-300 mt-2 leading-relaxed">
                                                            {plan.hint}
                                                        </p>
                                                    </button>
                                                );
                                            })}
                                        </div>
                                        {renderPapersCheckoutButton()}
                                    </div>
                                )}
                                </div>
                            </div>
                        </div>
                    ) : viewingToolId === PE_PROFIT_TOOL_ID ? (
                        <div className="max-w-2xl mx-auto w-full">
                            <div className="premium-card-border rounded-2xl p-[2px]">
                                <div className="bg-[#111622] rounded-2xl p-6 md:p-8">
                                    <button
                                        type="button"
                                        onClick={() => setViewingToolId(null)}
                                        className="mb-5 text-slate-400 hover:text-[#E6C299] text-sm transition-colors"
                                    >
                                        ← Zurück zur Übersicht
                                    </button>

                                    <img
                                        src="/Premium-Tools/pe-profit.png"
                                        alt="PEProfit Premium Artwork"
                                        className="w-full h-48 sm:h-56 object-cover rounded-md mb-5"
                                    />

                                    {renderPremiumToolTitle('Profit', 'text-2xl')}
                                    <p className="text-xs text-slate-500 uppercase tracking-widest mb-3">Gewinnanalyse</p>
                                    <p className="text-slate-300 text-sm leading-relaxed mb-6">
                                        Bankenkonforme Rentabilitätsvorschau mit Ist-/Prognose-Vergleich, P&amp;L-Report
                                        und rechtssicherer Unternehmer-Bestätigung.
                                    </p>

                                    <CoachingPackageHintBox />

                                    {unlockedPremiumTools.includes(PE_PROFIT_TOOL_ID) ? (
                                        <div className="space-y-4">
                                            <div className="rounded-xl border border-emerald-500/40 bg-emerald-500/10 px-4 py-4 text-center">
                                                <p className="text-emerald-400 font-bold text-sm">✅ Erfolgreich aktiviert.</p>
                                                {getToolPlanType(PE_PROFIT_TOOL_ID) && (
                                                    <p className="mt-1 text-xs text-emerald-200/80">
                                                        Aktiver Tarif:{' '}
                                                        {getToolPlanType(PE_PROFIT_TOOL_ID) === 'yearly'
                                                            ? '659 € Einmalzahlung / Jahr'
                                                            : '55 € Monatsabo'}
                                                    </p>
                                                )}
                                            </div>
                                            <div>
                                                <p className="mb-2 text-[10px] font-bold uppercase tracking-widest text-slate-400">
                                                    Tarifübersicht
                                                </p>
                                                {renderDualPlanGrid(
                                                    PE_PROFIT_PLANS,
                                                    selectedProfitPlan,
                                                    setSelectedProfitPlan,
                                                    'readonly',
                                                    getToolPlanType(PE_PROFIT_TOOL_ID)
                                                )}
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => openPremiumTool(PE_PROFIT_TOOL_ID)}
                                                className={CHECKOUT_BUTTON_CLASS}
                                            >
                                                Zum Rentabilitäts-Report
                                            </button>
                                            {renderCancelSection(PE_PROFIT_TOOL_ID, 'PEProfit')}
                                        </div>
                                    ) : (
                                        <div className="space-y-4">
                                            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
                                                Tarif wählen
                                            </p>
                                            {renderDualPlanGrid(
                                                PE_PROFIT_PLANS,
                                                selectedProfitPlan,
                                                setSelectedProfitPlan,
                                                'select'
                                            )}
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    void handleDualCheckout(
                                                        PE_PROFIT_TOOL_ID,
                                                        selectedProfitPlan,
                                                        'PEProfit'
                                                    )
                                                }
                                                disabled={isCheckingOut}
                                                className={CHECKOUT_BUTTON_CLASS}
                                            >
                                                {isCheckingOut ? (
                                                    <>
                                                        <Loader2 size={16} className="animate-spin" />
                                                        Wird freigeschaltet...
                                                    </>
                                                ) : selectedProfitPlan === 'yearly' ? (
                                                    'Einmalzahlung wählen (Mock-Checkout)'
                                                ) : (
                                                    'Monatsabo aktivieren (Mock-Checkout)'
                                                )}
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    ) : viewingToolId === PE_START_TOOL_ID ? (
                        <div className="max-w-2xl mx-auto w-full">
                            <div className="premium-card-border rounded-2xl p-[2px]">
                                <div className="bg-[#111622] rounded-2xl p-6 md:p-8">
                                    <button
                                        type="button"
                                        onClick={() => setViewingToolId(null)}
                                        className="mb-5 text-slate-400 hover:text-[#E6C299] text-sm transition-colors"
                                    >
                                        ← Zurück zur Übersicht
                                    </button>

                                    <img
                                        src="/Premium-Tools/pe-start.png"
                                        alt="PEStart Premium Artwork"
                                        className="w-full h-48 sm:h-56 object-cover rounded-md mb-5"
                                    />

                                    {renderPremiumToolTitle('Start', 'text-2xl')}
                                    <p className="text-sm font-bold text-white mb-1">
                                        Businessplan und Planungsanalyse für Existenzgründer und Salonübernahmen
                                    </p>
                                    <p className="text-xs text-slate-500 uppercase tracking-widest mb-3">
                                        Gründung · Übernahme · Bankenfähige Planung
                                    </p>
                                    <p className="text-slate-300 text-sm leading-relaxed mb-4">
                                        Totpunkt, Anlaufphase, Förderungen und bankenfähiger Businessplan —
                                        mit aktiver Coach-Begleitung.
                                    </p>

                                    <CoachingPackageHintBox />

                                    {unlockedPremiumTools.includes(PE_START_TOOL_ID) ? (
                                        <div className="space-y-4">
                                            <div className="rounded-xl border border-emerald-500/40 bg-emerald-500/10 px-4 py-4 text-center">
                                                <p className="text-emerald-400 font-bold text-sm">
                                                    ✅ Erfolgreich aktiviert.
                                                </p>
                                                {getToolPlanType(PE_START_TOOL_ID) && (
                                                    <p className="mt-1 text-xs text-emerald-200/80">
                                                        Aktiver Tarif:{' '}
                                                        {getToolPlanType(PE_START_TOOL_ID) === 'yearly'
                                                            ? '549 € Einmalzahlung / Jahr'
                                                            : '46 € Monatsabo'}
                                                    </p>
                                                )}
                                            </div>
                                            <div>
                                                <p className="mb-2 text-[10px] font-bold uppercase tracking-widest text-slate-400">
                                                    Tarifübersicht
                                                </p>
                                                {renderDualPlanGrid(
                                                    PE_START_PLANS,
                                                    selectedStartPlan,
                                                    setSelectedStartPlan,
                                                    'readonly',
                                                    getToolPlanType(PE_START_TOOL_ID)
                                                )}
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => openPremiumTool(PE_START_TOOL_ID)}
                                                className={CHECKOUT_BUTTON_CLASS}
                                            >
                                                Zum Businessplan
                                            </button>
                                            {renderCancelSection(PE_START_TOOL_ID, 'PEStart')}
                                        </div>
                                    ) : (
                                        <div className="space-y-4">
                                            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
                                                Tarif wählen
                                            </p>
                                            {renderDualPlanGrid(
                                                PE_START_PLANS,
                                                selectedStartPlan,
                                                setSelectedStartPlan,
                                                'select'
                                            )}
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    void handleDualCheckout(
                                                        PE_START_TOOL_ID,
                                                        selectedStartPlan,
                                                        'PEStart'
                                                    )
                                                }
                                                disabled={isCheckingOut}
                                                className={CHECKOUT_BUTTON_CLASS}
                                            >
                                                {isCheckingOut ? (
                                                    <>
                                                        <Loader2 size={16} className="animate-spin" />
                                                        Wird freigeschaltet...
                                                    </>
                                                ) : selectedStartPlan === 'yearly' ? (
                                                    'Einmalzahlung wählen (Mock-Checkout)'
                                                ) : (
                                                    'Monatsabo aktivieren (Mock-Checkout)'
                                                )}
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    ) : viewingToolId === PE_RENT_TOOL_ID ? (
                        <div className="max-w-2xl mx-auto w-full">
                            <div className="premium-card-border rounded-2xl p-[2px]">
                                <div className="bg-[#111622] rounded-2xl p-6 md:p-8">
                                    <button
                                        type="button"
                                        onClick={() => setViewingToolId(null)}
                                        className="mb-5 text-slate-400 hover:text-[#E6C299] text-sm transition-colors"
                                    >
                                        ← Zurück zur Übersicht
                                    </button>

                                    <img
                                        src="/Premium-Tools/pe-rent.png"
                                        alt="PERent Premium Artwork"
                                        className="w-full h-48 sm:h-56 object-cover rounded-md mb-5"
                                    />

                                    {renderPremiumToolTitle('Rent', 'text-2xl')}
                                    <p className="text-xs text-slate-500 uppercase tracking-widest mb-3">
                                        Salon-Kalkulation
                                    </p>
                                    <p className="text-slate-300 text-sm leading-relaxed mb-6">
                                        Berechne die wirtschaftlich korrekte Stuhlmiete aus deinen realen
                                        Salon-Gemeinkosten und produktiven Teamstunden. Mit Wochen-/Monatsmodus,
                                        optionalen Kostenblöcken und druckfertigem Nachweis.
                                    </p>

                                    <ul className="text-slate-400 text-sm space-y-2 mb-6">
                                        <li className="flex items-start gap-2">
                                            <Check className="w-4 h-4 text-[#E6C299] shrink-0 mt-0.5" />
                                            Live-Anbindung an P1-Gemeinkosten &amp; Teamstunden
                                        </li>
                                        <li className="flex items-start gap-2">
                                            <Check className="w-4 h-4 text-[#E6C299] shrink-0 mt-0.5" />
                                            Wochen-, Monats- und Faktor-Eingabe
                                        </li>
                                        <li className="flex items-start gap-2">
                                            <Check className="w-4 h-4 text-[#E6C299] shrink-0 mt-0.5" />
                                            Druckfertige Kalkulationsübersicht
                                        </li>
                                        <li className="flex items-start gap-2">
                                            <Check className="w-4 h-4 text-[#E6C299] shrink-0 mt-0.5" />
                                            Inklusive Coach-Betreuung bei der Umsetzung
                                        </li>
                                    </ul>

                                    <CoachingPackageHintBox />

                                    {unlockedPremiumTools.includes(PE_RENT_TOOL_ID) ? (
                                        <div className="space-y-4">
                                            <div className="rounded-xl border border-emerald-500/40 bg-emerald-500/10 px-4 py-4 text-center">
                                                <p className="text-emerald-400 font-bold text-sm">
                                                    ✅ Erfolgreich aktiviert.
                                                </p>
                                                {peRentLicenseType && (
                                                    <p className="mt-1 text-xs text-emerald-200/80">
                                                        Aktiver Tarif:{' '}
                                                        {peRentLicenseType === 'single_300'
                                                            ? '199 € Einmalzahlung / Jahr'
                                                            : '14,99 € Monatsabo'}
                                                    </p>
                                                )}
                                            </div>
                                            <div>
                                                <p className="mb-2 text-[10px] font-bold uppercase tracking-widest text-slate-400">
                                                    Tarifübersicht
                                                </p>
                                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                                    {PE_RENT_PLANS.map((plan) => {
                                                        const isActive = peRentLicenseType === plan.id;
                                                        return (
                                                            <div
                                                                key={plan.id}
                                                                className={`relative rounded-xl px-4 py-4 text-left border ${
                                                                    isActive || plan.recommended
                                                                        ? 'border-2 border-[#E6C299] bg-[#E6C299]/15'
                                                                        : 'border-slate-700 bg-[#0a0b10]'
                                                                }`}
                                                            >
                                                                {plan.recommended && plan.advantageBadge ? (
                                                                    <span className="absolute -top-2.5 right-2 max-w-[calc(100%-0.5rem)] whitespace-normal text-center px-2 py-0.5 rounded-full bg-[#E6C299] text-black text-[10px] font-black tracking-wide leading-tight">
                                                                        {plan.advantageBadge}
                                                                    </span>
                                                                ) : plan.recommended ? (
                                                                    <span className="absolute -top-2 right-3 whitespace-nowrap px-2 py-0.5 rounded-full bg-[#E6C299] text-black text-[10px] font-black tracking-wide">
                                                                        Empfohlen
                                                                    </span>
                                                                ) : null}
                                                                <p className="text-xs uppercase tracking-widest mb-1 font-bold text-[#E6C299]">
                                                                    {plan.label}
                                                                </p>
                                                                <p className="text-lg font-black text-white leading-tight">
                                                                    {plan.price}
                                                                    <span className="text-xs font-semibold text-slate-400">
                                                                        {' '}
                                                                        {plan.suffix}
                                                                    </span>
                                                                </p>
                                                                <p className="text-sm text-slate-300 mt-2 leading-relaxed">
                                                                    {plan.hint}
                                                                </p>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => openPremiumTool(PE_RENT_TOOL_ID)}
                                                className={CHECKOUT_BUTTON_CLASS}
                                            >
                                                Zur Stuhlmiete-Kalkulation
                                            </button>
                                            {renderCancelSection(PE_RENT_TOOL_ID, 'PERent')}
                                        </div>
                                    ) : (
                                        <div className="space-y-4">
                                            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
                                                Tarif wählen
                                            </p>
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                                {PE_RENT_PLANS.map((plan) => {
                                                    const isSelected = selectedRentPlan === plan.id;
                                                    return (
                                                        <button
                                                            key={plan.id}
                                                            type="button"
                                                            onClick={() => setSelectedRentPlan(plan.id)}
                                                            className={`relative rounded-xl px-4 py-4 text-left transition-all ${
                                                                isSelected
                                                                    ? 'border-2 border-[#E6C299] bg-[#E6C299]/15 shadow-[0_0_20px_rgba(230,194,153,0.2)]'
                                                                    : 'border border-slate-700 bg-[#0a0b10] hover:border-slate-500'
                                                            }`}
                                                        >
                                                            {plan.recommended && plan.advantageBadge ? (
                                                                <span className="absolute -top-2.5 right-2 max-w-[calc(100%-0.5rem)] whitespace-normal text-center px-2 py-0.5 rounded-full bg-[#E6C299] text-black text-[10px] font-black tracking-wide leading-tight">
                                                                    {plan.advantageBadge}
                                                                </span>
                                                            ) : plan.recommended ? (
                                                                <span className="absolute -top-2 right-3 whitespace-nowrap px-2 py-0.5 rounded-full bg-[#E6C299] text-black text-[10px] font-black tracking-wide">
                                                                    Empfohlen
                                                                </span>
                                                            ) : null}
                                                            <p
                                                                className={`text-xs uppercase tracking-widest mb-1 font-bold ${
                                                                    isSelected
                                                                        ? 'text-[#E6C299]'
                                                                        : 'text-slate-400'
                                                                }`}
                                                            >
                                                                {plan.label}
                                                            </p>
                                                            <p className="text-lg font-black text-white leading-tight">
                                                                {plan.price}
                                                                <span className="text-xs font-semibold text-slate-400">
                                                                    {' '}
                                                                    {plan.suffix}
                                                                </span>
                                                            </p>
                                                            <p className="text-sm text-slate-300 mt-2 leading-relaxed">
                                                                {plan.hint}
                                                            </p>
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                            <button
                                                type="button"
                                                onClick={async () => {
                                                    setIsCheckingOut(true);
                                                    await new Promise((resolve) => setTimeout(resolve, 1500));
                                                    setPERentLicense(selectedRentPlan);
                                                    showNotification(
                                                        selectedRentPlan === 'single_300'
                                                            ? 'PERent freigeschaltet (199 € / Jahr).'
                                                            : 'PERent freigeschaltet (14,99 € / Monat).',
                                                        'success'
                                                    );
                                                    setIsCheckingOut(false);
                                                }}
                                                disabled={isCheckingOut}
                                                className={CHECKOUT_BUTTON_CLASS}
                                            >
                                                {isCheckingOut ? (
                                                    <>
                                                        <Loader2 size={16} className="animate-spin" />
                                                        Wird freigeschaltet...
                                                    </>
                                                ) : selectedRentPlan === 'single_300' ? (
                                                    'Einmalzahlung wählen (Mock-Checkout)'
                                                ) : (
                                                    'Monatsabo aktivieren (Mock-Checkout)'
                                                )}
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    ) : viewingToolId === PE_SCOUT_TOOL_ID ? (
                        <div className="max-w-2xl mx-auto w-full">
                            <div className="premium-card-border rounded-2xl p-[2px]">
                                <div className="bg-[#111622] rounded-2xl p-6 md:p-8">
                                    <button
                                        type="button"
                                        onClick={() => setViewingToolId(null)}
                                        className="mb-5 text-slate-400 hover:text-[#E6C299] text-sm transition-colors"
                                    >
                                        ← Zurück zur Übersicht
                                    </button>

                                    <img
                                        src="/Premium-Tools/pe-job.png"
                                        alt="PEScout Premium Artwork"
                                        className="w-full h-48 sm:h-56 object-cover rounded-md mb-5"
                                    />

                                    {renderPremiumToolTitle('Scout', 'text-2xl')}
                                    <p className="text-xs text-slate-500 uppercase tracking-widest mb-3">
                                        Mitarbeiterakquise · Recruiting
                                    </p>
                                    <p className="text-slate-300 text-sm leading-relaxed mb-4">
                                        <strong className="text-white">Mitarbeiter finden und einstellen</strong> –
                                        das ist PEScout. Vollwertige Masterclass, KI-Jobtexte, Bild-KI und
                                        Interview-Protokolle, plus Leitfaden wo deine Anzeigen später laufen.
                                    </p>

                                    <ul className="text-slate-400 text-sm space-y-2 mb-6">
                                        <li>✓ Mitarbeiterakquise von A bis Z – keine Ratei</li>
                                        <li>✓ Vollwertiges Masterclass-Seminar (Videos + Module)</li>
                                        <li>✓ KI-Textgenerator für Job-Ads ohne Floskeln</li>
                                        <li>✓ Bild-KI für Kampagnenmotive</li>
                                        <li>✓ Interview-Navigator (Fachkraft, Azubi, Assistenz, Rezeption)</li>
                                        <li>✓ Veröffentlichungs-Leitfaden (FB-Gruppen, Kleinanzeigen, Arbeitsamt…)</li>
                                    </ul>

                                    {unlockedPremiumTools.includes(PE_SCOUT_TOOL_ID) ? (
                                        <div className="space-y-4">
                                            <div className="rounded-xl border border-emerald-500/40 bg-emerald-500/10 px-4 py-4 text-center">
                                                <p className="text-emerald-400 font-bold text-sm">
                                                    ✅ Erfolgreich aktiviert.
                                                </p>
                                                {getToolPlanType(PE_SCOUT_TOOL_ID) && (
                                                    <p className="mt-1 text-xs text-emerald-200/80">
                                                        Aktiver Tarif:{' '}
                                                        {getToolPlanType(PE_SCOUT_TOOL_ID) === 'yearly'
                                                            ? '380 € Einmalzahlung / Jahr'
                                                            : '29,99 € Monatsabo'}
                                                    </p>
                                                )}
                                            </div>
                                            <div>
                                                <p className="mb-2 text-[10px] font-bold uppercase tracking-widest text-slate-400">
                                                    Tarifübersicht
                                                </p>
                                                {renderDualPlanGrid(
                                                    PE_SCOUT_PLANS,
                                                    selectedScoutPlan,
                                                    setSelectedScoutPlan,
                                                    'readonly',
                                                    getToolPlanType(PE_SCOUT_TOOL_ID)
                                                )}
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => openPremiumTool(PE_SCOUT_TOOL_ID)}
                                                className={CHECKOUT_BUTTON_CLASS}
                                            >
                                                Zur Mitarbeiterakquise
                                            </button>
                                            {renderCancelSection(PE_SCOUT_TOOL_ID, 'PEScout')}
                                        </div>
                                    ) : (
                                        <div className="space-y-4">
                                            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
                                                Tarif wählen
                                            </p>
                                            {renderDualPlanGrid(
                                                PE_SCOUT_PLANS,
                                                selectedScoutPlan,
                                                setSelectedScoutPlan,
                                                'select'
                                            )}
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    void handleDualCheckout(
                                                        PE_SCOUT_TOOL_ID,
                                                        selectedScoutPlan,
                                                        'PEScout'
                                                    )
                                                }
                                                disabled={isCheckingOut}
                                                className={CHECKOUT_BUTTON_CLASS}
                                            >
                                                {isCheckingOut ? (
                                                    <>
                                                        <Loader2 size={16} className="animate-spin" />
                                                        Wird freigeschaltet...
                                                    </>
                                                ) : selectedScoutPlan === 'yearly' ? (
                                                    'Einmalzahlung wählen (Mock-Checkout)'
                                                ) : (
                                                    'Monatsabo aktivieren (Mock-Checkout)'
                                                )}
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    ) : viewingToolId === PE_PLAN_TOOL_ID ? (
                        <div className="max-w-2xl mx-auto w-full">
                            <div className="premium-card-border rounded-2xl p-[2px]">
                                <div className="bg-[#111622] rounded-2xl p-6 md:p-8">
                                    <button
                                        type="button"
                                        onClick={() => setViewingToolId(null)}
                                        className="mb-5 text-slate-400 hover:text-[#E6C299] text-sm transition-colors"
                                    >
                                        ← Zurück zur Übersicht
                                    </button>

                                    <img
                                        src="/Premium-Tools/pe-plan.png"
                                        alt="PEPlan Premium Artwork"
                                        className="w-full h-48 sm:h-56 object-cover rounded-md mb-5"
                                    />

                                    <div className="text-2xl text-[#E6C299] mb-1">
                                        <span className="italic">PE</span>
                                        <span className="font-bold">Plan – Strategischer Planungsrechner</span>
                                    </div>
                                    <p className="text-xs text-[#E6C299]/80 font-bold uppercase tracking-widest mb-3">
                                        Echtzeit-Simulation für Personal, Inhaber-Stuhlzeit, Kosten &amp; Preisanpassungen
                                    </p>
                                    <p className="text-slate-300 text-sm leading-relaxed mb-4">
                                        Vollwertiges Simulationswerkzeug für Inhaber, um Personalfluktuation, Abgänge mit Auffangquote, Inhaber-Rückzug, Zusatzinvestitionen und punktgenaue Preisanpassungen vorab risikofrei im Salon durchzuspielen.
                                    </p>

                                    <ul className="text-slate-400 text-sm space-y-2 mb-6">
                                        <li>✓ 5 Szenario-Hebel: Neuanstellung, Kündigung &amp; Team-Auffangquote, Inhaber-Stuhlzeit, Investitionen, Preiserhöhung</li>
                                        <li>✓ Automatisch austarierter Kostendeckungsfaktor (KDF) &amp; reaktiver Lohnfaktor</li>
                                        <li>✓ Gesamtsalon-Umsatzziele (Netto &amp; Brutto, mit und ohne 10 % Rücklagenbindung)</li>
                                        <li>✓ Spiegelung der neuen Kennzahlen auf alle Mitarbeiter-Sollumsätze (bei 75 % Auslastung)</li>
                                        <li>✓ Integrierter Dienstleistungs-Kalkulator mit Materialmitnahme und Preis-Barometer</li>
                                    </ul>

                                    {unlockedPremiumTools.includes(PE_PLAN_TOOL_ID) ? (
                                        <div className="space-y-4">
                                            <div className="rounded-xl border border-emerald-500/40 bg-emerald-500/10 px-4 py-4 text-center">
                                                <p className="text-emerald-400 font-bold text-sm">
                                                    ✅ Erfolgreich aktiviert.
                                                </p>
                                                {getToolPlanType(PE_PLAN_TOOL_ID) && (
                                                    <p className="mt-1 text-xs text-emerald-200/80">
                                                        Aktiver Tarif:{' '}
                                                        {getToolPlanType(PE_PLAN_TOOL_ID) === 'yearly'
                                                            ? '299,00 € netto einmalig (Lifetime / Jahresbeitrag)'
                                                            : '24,99 € netto / Monat'}
                                                    </p>
                                                )}
                                            </div>
                                            <div>
                                                <p className="mb-2 text-[10px] font-bold uppercase tracking-widest text-slate-400">
                                                    Tarifübersicht
                                                </p>
                                                {renderDualPlanGrid(
                                                    PE_PLAN_PLANS,
                                                    selectedPlanPlan,
                                                    setSelectedPlanPlan,
                                                    'readonly',
                                                    getToolPlanType(PE_PLAN_TOOL_ID)
                                                )}
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => openPremiumTool(PE_PLAN_TOOL_ID)}
                                                className={CHECKOUT_BUTTON_CLASS}
                                            >
                                                Zum Planungsrechner
                                            </button>
                                            {renderCancelSection(PE_PLAN_TOOL_ID, 'PEPlan')}
                                        </div>
                                    ) : (
                                        <div className="space-y-4">
                                            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
                                                Tarif wählen
                                            </p>
                                            {renderDualPlanGrid(
                                                PE_PLAN_PLANS,
                                                selectedPlanPlan,
                                                setSelectedPlanPlan,
                                                'select'
                                            )}
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    void handleDualCheckout(
                                                        PE_PLAN_TOOL_ID,
                                                        selectedPlanPlan,
                                                        'PEPlan'
                                                    )
                                                }
                                                disabled={isCheckingOut}
                                                className={CHECKOUT_BUTTON_CLASS}
                                            >
                                                {isCheckingOut ? (
                                                    <>
                                                        <Loader2 size={16} className="animate-spin" />
                                                        Wird freigeschaltet...
                                                    </>
                                                ) : selectedPlanPlan === 'yearly' ? (
                                                    'Einmalkauf 299,00 € wählen (Mock-Checkout)'
                                                ) : (
                                                    'Monats-Lizenz 24,99 € aktivieren (Mock-Checkout)'
                                                )}
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    ) : viewingTool ? (
                        <div className="max-w-2xl mx-auto w-full">
                            <div className="premium-card-border rounded-2xl p-[2px]">
                                <div className="bg-[#111622] rounded-2xl p-6 md:p-8">
                                    <button
                                        type="button"
                                        onClick={() => setViewingToolId(null)}
                                        className="mb-5 text-slate-400 hover:text-[#E6C299] text-sm transition-colors"
                                    >
                                        ← Zurück zur Übersicht
                                    </button>

                                    {viewingTool.bannerSrc ? (
                                        <img
                                            src={viewingTool.bannerSrc}
                                            alt={`PE${viewingTool.nameSuffix} Premium Artwork`}
                                            className="w-full h-48 sm:h-56 object-cover rounded-md mb-5"
                                        />
                                    ) : (
                                        <div className="w-full h-48 sm:h-56 bg-gradient-to-br from-[#0a0b10] via-[#111622] to-[#0f1015] border border-[#E6C299]/10 rounded-md mb-5 flex items-center justify-center">
                                            {renderPremiumToolTitle(viewingTool.nameSuffix, 'text-3xl')}
                                        </div>
                                    )}

                                    {renderPremiumToolTitle(viewingTool.nameSuffix, 'text-2xl')}
                                    {viewingTool.category && (
                                        <p className="text-xs text-slate-500 uppercase tracking-widest mb-3">
                                            {viewingTool.category}
                                        </p>
                                    )}
                                    <p className="text-slate-300 text-sm leading-relaxed mb-4">
                                        {viewingTool.detailDescription ??
                                            viewingTool.shortDescription ??
                                            viewingTool.description}
                                    </p>

                                    {viewingTool.coachingNote && (
                                        <CoachingPackageHintBox note={viewingTool.coachingNote} />
                                    )}

                                    {unlockedPremiumTools.includes(viewingTool.id) ? (
                                        <div className="space-y-4">
                                            <div className="rounded-xl border border-emerald-500/40 bg-emerald-500/10 px-4 py-4 text-center">
                                                <p className="text-emerald-400 font-bold text-sm">
                                                    ✅ Erfolgreich aktiviert.
                                                </p>
                                                {viewingTool.id === PE_FACTS_ID && getToolPlanType(PE_FACTS_ID) && (
                                                    <p className="mt-1 text-xs text-emerald-200/80">
                                                        Aktiver Tarif:{' '}
                                                        {getToolPlanType(PE_FACTS_ID) === 'yearly'
                                                            ? '189 € Einmalzahlung / Jahr'
                                                            : '11,99 € Monatsabo'}
                                                    </p>
                                                )}
                                            </div>
                                            {viewingTool.id === PE_FACTS_ID ? (
                                                <div>
                                                    <p className="mb-2 text-[10px] font-bold uppercase tracking-widest text-slate-400">
                                                        Tarifübersicht
                                                    </p>
                                                    {renderDualPlanGrid(
                                                        PE_FACTS_PLANS,
                                                        selectedFactsPlan,
                                                        setSelectedFactsPlan,
                                                        'readonly',
                                                        getToolPlanType(PE_FACTS_ID)
                                                    )}
                                                </div>
                                            ) : viewingTool.price != null ? (
                                                <div>
                                                    <p className="mb-2 text-[10px] font-bold uppercase tracking-widest text-slate-400">
                                                        Tarifübersicht
                                                    </p>
                                                    <div className="max-w-md mx-auto">
                                                        <div className="relative rounded-xl px-4 py-4 text-left border-2 border-[#E6C299] bg-[#E6C299]/15 shadow-[0_0_20px_rgba(230,194,153,0.2)]">
                                                            <p className="text-xs uppercase tracking-widest mb-2 font-bold text-[#E6C299]">
                                                                Monatsabo inkl. Premium-Coaching
                                                            </p>
                                                            <p className="text-lg font-black text-white leading-tight">
                                                                {viewingTool.price} €
                                                            </p>
                                                            <p className="text-xs font-semibold text-slate-300 mt-1">
                                                                / Monat · Abo verlängert sich ab Freischaltung automatisch, nach 12 Monaten monatlich kündbar
                                                            </p>
                                                        </div>
                                                    </div>
                                                </div>
                                            ) : null}
                                            {viewingTool.routeKey ? (
                                                <button
                                                    type="button"
                                                    onClick={() => openPremiumTool(viewingTool.routeKey!)}
                                                    className={CHECKOUT_BUTTON_CLASS}
                                                >
                                                    {viewingTool.ctaButtonText || 'Öffnen / Verwalten'}
                                                </button>
                                            ) : viewingTool.openAsModal && viewingTool.id === PE_FACTS_ID ? (
                                                <button
                                                    type="button"
                                                    onClick={() => setIsPeFactsOpen(true)}
                                                    className={CHECKOUT_BUTTON_CLASS}
                                                >
                                                    {viewingTool.ctaButtonText || 'Tool öffnen'}
                                                </button>
                                            ) : null}
                                            {renderCancelSection(viewingTool.id, `PE${viewingTool.nameSuffix}`)}
                                        </div>
                                    ) : viewingTool.id === PE_FACTS_ID && !viewingTool.comingSoon ? (
                                        <div className="space-y-4">
                                            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
                                                Tarif wählen
                                            </p>
                                            {renderDualPlanGrid(
                                                PE_FACTS_PLANS,
                                                selectedFactsPlan,
                                                setSelectedFactsPlan,
                                                'select'
                                            )}
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    void handleDualCheckout(
                                                        PE_FACTS_ID,
                                                        selectedFactsPlan,
                                                        'PEFacts'
                                                    )
                                                }
                                                disabled={isCheckingOut}
                                                className={CHECKOUT_BUTTON_CLASS}
                                            >
                                                {isCheckingOut ? (
                                                    <>
                                                        <Loader2 size={16} className="animate-spin" />
                                                        Wird freigeschaltet...
                                                    </>
                                                ) : selectedFactsPlan === 'yearly' ? (
                                                    'Einmalzahlung 189 € wählen (Mock-Checkout)'
                                                ) : (
                                                    'Monatsabo 11,99 € aktivieren (Mock-Checkout)'
                                                )}
                                            </button>
                                        </div>
                                    ) : viewingTool.price != null && !viewingTool.comingSoon ? (
                                        <div className="space-y-3">
                                            <div className="max-w-md mx-auto">
                                                <div className="relative rounded-xl px-4 py-4 text-left border-2 border-[#E6C299] bg-[#E6C299]/15 shadow-[0_0_20px_rgba(230,194,153,0.2)]">
                                                    <p className="text-xs uppercase tracking-widest mb-2 font-bold text-[#E6C299]">
                                                        Monatsabo inkl. Premium-Coaching
                                                    </p>
                                                    <p className="text-lg font-black text-white leading-tight">
                                                        {viewingTool.price} €
                                                    </p>
                                                    <p className="text-xs font-semibold text-slate-300 mt-1">
                                                        / Monat · Abo verlängert sich ab Freischaltung automatisch, nach 12 Monaten monatlich kündbar
                                                    </p>
                                                </div>
                                            </div>
                                            <button
                                                type="button"
                                                onClick={async () => {
                                                    setIsCheckingOut(true);
                                                    await new Promise((resolve) => setTimeout(resolve, 1500));
                                                    unlockTool(viewingTool.id);
                                                    showNotification(
                                                        `PE${viewingTool.nameSuffix} wurde erfolgreich freigeschaltet!`,
                                                        'success'
                                                    );
                                                    setIsCheckingOut(false);
                                                }}
                                                disabled={isCheckingOut}
                                                className={CHECKOUT_BUTTON_CLASS}
                                            >
                                                {isCheckingOut ? (
                                                    <>
                                                        <Loader2 size={16} className="animate-spin" />
                                                        Wird freigeschaltet...
                                                    </>
                                                ) : (
                                                    'Jetzt freischalten (Mock-Checkout)'
                                                )}
                                            </button>
                                        </div>
                                    ) : (
                                        (viewingTool.openAsModal && viewingTool.id === PE_FACTS_ID && (
                                            <button
                                                type="button"
                                                onClick={() => setIsPeFactsOpen(true)}
                                                className={`${CHECKOUT_BUTTON_CLASS} mt-2`}
                                            >
                                                {viewingTool.ctaButtonText || 'Tool öffnen'}
                                            </button>
                                        )) ||
                                        (viewingTool.routeKey && (
                                            <button
                                                type="button"
                                                onClick={() => openPremiumTool(viewingTool.routeKey!)}
                                                className={`${CHECKOUT_BUTTON_CLASS} mt-2`}
                                            >
                                                {viewingTool.ctaButtonText || 'Tool öffnen'}
                                            </button>
                                        ))
                                    )}
                                </div>
                            </div>
                        </div>
                    ) : null}
                </div>
            </div>
            <PeFactsModal
                isOpen={isPeFactsOpen}
                onClose={() => setIsPeFactsOpen(false)}
                showNotification={showNotification}
            />
        </>,
        document.body
    );
};

export default PremiumModal;
